import { useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import type { components } from '../../lib/api/schema.gen'

type Sourceable = components['schemas']['Sourceable']

/**
 * Thrown by a {@link ConfigurableResolveService.resolve} implementation to
 * signal "this SKU names no real, orderable point" (a 404) — distinct from
 * every other failure (`error`), which the hook never conflates with it.
 * Exported so a test fake (or any future adapter) can raise the SAME signal
 * {@link bffResolveConfigurable} does, rather than reaching into this
 * module's internals.
 */
export class ConfigurableNotAvailableError extends Error {}

function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

export interface ConfigurableResolveService {
  resolve(sku: string): Promise<Sourceable>
}

async function bffResolveConfigurable(sku: string): Promise<Sourceable> {
  const { data, error } = await apiClient.GET('/catalog/configurable/{sku}', {
    params: { path: { sku } },
  })
  if (data) return data.sourceable
  if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
    throw new ConfigurableNotAvailableError()
  }
  throw new Error(problemTitle(error, 'Could not resolve that configuration.'))
}

export const defaultConfigurableResolveService: ConfigurableResolveService = {
  resolve: bffResolveConfigurable,
}

export type ConfigurableResolveState =
  /** No legal SKU picked yet (ADR-0097 8.2 "Unresolved") — no call made. */
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'resolved'; readonly sourceable: Sourceable }
  /** The composed SKU is a 404 — "not available at this value" (ADR-0097 8.2). */
  | { readonly status: 'not-available' }
  /** Anything else (503 datastore-unavailable, network failure, etc). */
  | { readonly status: 'error'; readonly message: string }

/**
 * The terminal step of the configurator core (ADR-0097 Decision 2 step 5/6):
 * resolves a composed literal SKU via the BFF's `GET /catalog/configurable/
 * {sku}` proxy — the SAME hook backs both Surface A and Surface B, since
 * they share one core. `sku === null` means "no legal value picked yet"
 * (see `validateAndComposeSku`) — idle, no round trip. Re-resolves whenever
 * `sku` changes; a stale in-flight resolve is ignored if `sku` changes again
 * before it settles.
 *
 * The resolved `Sourceable` is the SOLE source of truth for the concrete
 * item + its real price (ADR-0097 8.4 step 6) — callers must never
 * substitute a client-composed guess for it.
 */
export function useConfigurableResolve(
  sku: string | null,
  service: ConfigurableResolveService = defaultConfigurableResolveService,
): ConfigurableResolveState {
  const [state, setState] = useState<ConfigurableResolveState>({ status: 'idle' })
  const serviceRef = useRef(service)
  serviceRef.current = service

  useEffect(() => {
    if (sku === null) {
      setState({ status: 'idle' })
      return
    }
    let cancelled = false
    setState({ status: 'loading' })
    serviceRef.current
      .resolve(sku)
      .then((sourceable) => {
        if (!cancelled) setState({ status: 'resolved', sourceable })
      })
      .catch((error: unknown) => {
        if (cancelled) return
        if (error instanceof ConfigurableNotAvailableError) {
          setState({ status: 'not-available' })
          return
        }
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : 'Could not resolve that configuration.',
        })
      })
    return () => {
      cancelled = true
    }
    // `service` is read only via the ref, so `sku` is the only real dependency.
  }, [sku])

  return state
}
